/**
 * Multi-sport events — an Olympics, a Commonwealth Games, an Asian Games.
 *
 * Their own place rather than a corner of Sports, because a Games is not one:
 * it belongs to no sport, and the sports belong to *it*. That inversion is the
 * whole reason `competition.sport_id` became optional.
 */

import { Link } from "react-router-dom";

import { NewEvent } from "@/components/events/NewEvent";
import { Card, Empty, PageHead, Pill, Problem, Section, Table, Td, Th } from "@/components/kit";
import { listEvents } from "@/lib/api/client";
import { useApi } from "@/spa/useApi";

export function EventsPage() {
  const { data: events, loading, error } = useApi(listEvents, [], "failed to load");

  if (error) return <Problem>Could not load events: {error}</Problem>;
  if (!events) {
    return <PageHead title="Events" note={loading ? "Loading…" : ""} actions={<NewEvent />} />;
  }

  return (
    <>
      <PageHead
        title="Events"
        note={
          events.length === 0
            ? "Nothing yet"
            : `${events.length} multi-sport event${events.length === 1 ? "" : "s"}`
        }
        actions={<NewEvent />}
      />

      <Section title="Multi-sport events">
        {events.length === 0 ? (
          <Card>
            <Empty
              title="No events yet"
              note="An Olympics, a Commonwealth Games, an Asian Games — anything that holds several sports at once."
              action={<NewEvent />}
            />
          </Card>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Event</Th>
                <Th>When</Th>
                <Th num>Sports</Th>
                <Th num>Fixtures</Th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id}>
                  <Td strong>
                    <Link
                      to={`/events/${event.code}`}
                      style={{ color: "inherit", textDecoration: "none" }}
                    >
                      {event.name}
                    </Link>
                  </Td>
                  <Td quiet>
                    {event.startDate ? new Date(event.startDate).toLocaleDateString() : "—"}
                  </Td>
                  <Td num>
                    {event.sports === 0 ? <Pill tone="warn">none yet</Pill> : event.sports}
                  </Td>
                  <Td num>{event.fixtures}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>
    </>
  );
}
